import { User } from 'lucide-react';
import { KDoreseniSeznam } from '../../layout/KDoreseni';
import { datum, pozadovaneCertifikaty, stavCertifikatu, zbyva, onboarding, vNastupu, SECTION_COLOR } from '../lide';
import { muted, linka } from './ui';

/**
 * Třetí sloupec karty zaměstnance — jen informuje (norma §1f): fotka, jak
 * dlouho je ve firmě, certifikace s platností a „K dořešení". Nic se v něm
 * needituje.
 *
 * Odpovídá na **„kdo to je a jak na tom je"**; levá karta na „co o něm
 * evidujeme". Podle té dělicí čáry tu od 2026-09-29 není stav ani typ úvazku
 * — obojí stálo na obrazovce dvakrát (stav jako odznak v hlavičce, úvazek
 * v Pracovních údajích, kde se i mění).
 *
 * Na mobilu se sloupec nekreslí; `PrehledZamestnance` a tytéž položky
 * „K dořešení" jdou do modalu za řádkem v hlavičce karty — tam bez fotky,
 * protože avatar stojí kousek nad ním v hlavičce.
 */

function veFirme(od) {
    if (!od) return null;
    const start = new Date(String(od).split('T')[0] + 'T00:00:00');
    const dnes = new Date();
    let mesicu = (dnes.getFullYear() - start.getFullYear()) * 12 + (dnes.getMonth() - start.getMonth());
    if (dnes.getDate() < start.getDate()) mesicu -= 1;
    if (mesicu < 1) {
        const dni = Math.max(0, Math.floor((dnes - start) / 86400000));
        return `${dni} ${dni === 1 ? 'den' : dni < 5 ? 'dny' : 'dní'}`;
    }
    const roky = Math.floor(mesicu / 12);
    const m = mesicu % 12;
    const r = roky ? `${roky} ${roky === 1 ? 'rok' : roky < 5 ? 'roky' : 'let'}` : '';
    return [r, m ? `${m} měs.` : ''].filter(Boolean).join(' ');
}

const Nadpis = ({ children, isDark, prvni }) => (
    <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', margin: prvni ? '0 0 6px' : '18px 0 6px' }}>{children}</p>
);

/** Velký portrét nahoře v panelu. Bez fotky iniciály, ne prázdné místo. */
function Portret({ z, isDark }) {
    const src = z.uzivatel?.avatar_custom_url || z.avatar_url;
    const styl = {
        width: '100%', aspectRatio: '9 / 10', borderRadius: 10, flexShrink: 0,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)'}`,
    };
    if (src) {
        return (
            <img src={src.startsWith('/') ? src : `/storage/${src}`} alt=""
                style={{ ...styl, objectFit: 'cover', objectPosition: z.uzivatel?.avatar_position || z.avatar_position || 'center', display: 'block' }} />
        );
    }
    // Bez fotky iniciály, ne silueta i iniciály pod sebou — obojí říká totéž.
    const iniciraly = `${z.jmeno?.[0] || ''}${z.prijmeni?.[0] || ''}`;
    return (
        <div className="flex items-center justify-center"
            style={{ ...styl, background: isDark ? 'linear-gradient(160deg, #24242c, #141418)' : 'var(--surface-elevated)' }}>
            {iniciraly
                ? <span className="font-bold" style={{ fontSize: 56, color: `${SECTION_COLOR}90`, letterSpacing: '0.04em' }}>{iniciraly}</span>
                : <User size={64} strokeWidth={1.2} style={{ color: `${SECTION_COLOR}70` }} />}
        </div>
    );
}

/** Číslo, které se nikde jinde na obrazovce neopakuje — proto velké. */
const Cislo = ({ popis, hodnota, vpravo = false }) => (
    <div style={{ textAlign: vpravo ? 'right' : 'left', minWidth: 0 }}>
        <div className="text-[10.5px] font-semibold uppercase" style={{ color: 'var(--text-secondary)', letterSpacing: '0.06em' }}>{popis}</div>
        <div className="text-[16px] font-bold truncate" style={{ color: 'var(--text-primary)', marginTop: 3 }}>{hodnota || '—'}</div>
    </div>
);

/**
 * Certifikát s pruhem, kolik z platnosti uteklo.
 *
 * Datum („do 7. 4. 2027") neřekne, jestli to hoří — pruh a „za 6 měs." ano.
 * Barvu nese jen problém, „brzy" je tučně (norma §3). Bez data vydání se pruh
 * nekreslí, jen text.
 */
export function Certifikat({ z, kod, typ, isDark }) {
    const s = stavCertifikatu(z, kod);
    const problem = s.stav === 'chybi' || s.stav === 'expirovano';
    const barva = problem ? '#f87171' : 'var(--text-primary)';
    const hodnota = s.stav === 'chybi' ? 'chybí' : s.stav === 'expirovano' ? 'expiroval' : zbyva(s.dni);

    return (
        <div style={{ padding: '8px 0 9px', borderBottom: `1px solid ${linka(isDark)}` }}
            title={s.cert?.platnost_do ? `Platí do ${datum(s.cert.platnost_do)}` : undefined}>
            <div className="flex items-center" style={{ gap: 8 }}>
                {typ?.odznak && <span className="text-[13px]" style={{ flexShrink: 0 }} aria-hidden="true">{typ.odznak}</span>}
                <span className="text-[12.5px] min-w-0 flex-1 truncate" style={{ color: barva, fontWeight: problem ? 600 : 400 }}>
                    {typ?.nazev || kod}
                </span>
                <span className="text-[12px] tabular-nums" style={{ color: problem ? '#f87171' : 'var(--text-secondary)', fontWeight: problem || s.stav === 'brzy' ? 700 : 600 }}>
                    {hodnota}
                </span>
            </div>
            {s.podil != null && (
                <div style={{ marginTop: 7, height: 4, borderRadius: 2, background: problem ? 'rgba(239,68,68,0.20)' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)') }}>
                    <div style={{
                        height: '100%', borderRadius: 2, width: `${Math.round(s.podil * 100)}%`,
                        background: problem ? '#EF4444' : (isDark ? `rgba(255,255,255,${s.stav === 'brzy' ? 0.75 : 0.38})` : `rgba(0,0,0,${s.stav === 'brzy' ? 0.55 : 0.28})`),
                    }} />
                </div>
            )}
        </div>
    );
}

export function PrehledZamestnance({ z, certTypy = [], checklist = null, isDark, padding = 0, portret = false }) {
    const kody = [...new Set([...pozadovaneCertifikaty(z), ...(z.certifikace || []).map(c => c.typ)])];
    const ob = onboarding(checklist);
    return (
        <div style={{ padding }}>
            {portret && <Portret z={z} isDark={isDark} />}

            <div className="flex items-start justify-between"
                style={{ gap: 12, marginTop: portret ? 16 : 0, paddingBottom: 14, borderBottom: `1px solid ${linka(isDark)}` }}>
                <Cislo popis="Ve firmě" hodnota={veFirme(z.datum_nastupu)} />
                {z.pocet_podribenych > 0 && <Cislo popis="Podřízení" hodnota={String(z.pocet_podribenych)} vpravo />}
            </div>

            {kody.length > 0 && (
                <>
                    <Nadpis isDark={isDark}>Certifikace</Nadpis>
                    {kody.map(kod => (
                        <Certifikat key={kod} z={z} kod={kod} typ={certTypy.find(x => x.kod === kod)} isDark={isDark} />
                    ))}
                </>
            )}

            {ob && vNastupu(z) && (
                <>
                    <Nadpis isDark={isDark}>Nástup</Nadpis>
                    <div className="flex items-center" style={{ gap: 8, minHeight: 28 }}>
                        <div style={{ flex: 1, height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
                            <div style={{ height: '100%', borderRadius: 3, width: `${(ob.hotovo / ob.celkem) * 100}%`, background: SECTION_COLOR }} />
                        </div>
                        <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{ob.hotovo}/{ob.celkem}</span>
                    </div>
                </>
            )}
        </div>
    );
}

export default function ZamestnanecPanel({ z, certTypy, checklist, kDoreseni = [], renderAkce, isDark, cardStyle }) {
    if (!z) return null;
    return (
        <div className="rounded-xl" style={{ ...cardStyle, padding: '14px 16px' }}>
            <PrehledZamestnance z={z} certTypy={certTypy} checklist={checklist} isDark={isDark} portret />
            <Nadpis isDark={isDark}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} renderAkce={renderAkce} prazdne="Vše v pořádku." />
        </div>
    );
}
